import {
  IChargeBasedPricingRuleset,
  IChargeBasedPricingRulesetCreateRequest,
  IChargeBasedPricingRulesetUpdateRequest,
} from "types/ChargeBasedPricing/ChargeBasedPricing";
import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
  IRulesetListParams,
} from "types/DefaultRules/DefaultRules";

import { api } from ".";

interface IListResponse {
  data: IChargeBasedPricingRuleset[];
  message?: string;
}

interface IItemResponse {
  data: IChargeBasedPricingRuleset;
  message?: string;
}

const chargeBasedPricingQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllChargeBasedPricing: builder.query<
      IListResponse,
      IRulesetListParams | null | void
    >({
      query: (params) => {
        let search = "";
        if (params?.applicability?.length) {
          search = `?applicability=${params.applicability.join(",")}`;
        } else if (params && params.isDefault !== undefined) {
          search = `?isDefault=${params.isDefault}`;
        }
        return `charge-based-pricing/all${search}`;
      },
      providesTags: ["ChargeBasedPricingRulesets"],
    }),
    getChargeBasedPricingById: builder.query<IItemResponse, string>({
      query: (id) => `charge-based-pricing/${id}`,
      providesTags: (_r, _e, id) => [
        { type: "ChargeBasedPricingRulesets", id },
      ],
    }),
    createChargeBasedPricing: builder.mutation<
      IItemResponse,
      IChargeBasedPricingRulesetCreateRequest
    >({
      query: (body) => ({
        url: "charge-based-pricing",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ChargeBasedPricingRulesets", "ContractFeeSchedules"],
    }),
    updateChargeBasedPricing: builder.mutation<
      IItemResponse,
      IChargeBasedPricingRulesetUpdateRequest
    >({
      query: (body) => ({
        url: `charge-based-pricing/${body.id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [
        "ChargeBasedPricingRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    deleteChargeBasedPricing: builder.mutation<void, string>({
      query: (id) => ({
        url: `charge-based-pricing/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "ChargeBasedPricingRulesets",
        "DefaultRulePropagation",
        "ContractFeeSchedules",
      ],
    }),
    cloneChargeBasedPricingAsDefault: builder.mutation<IItemResponse, string>({
      query: (id) => ({
        url: `charge-based-pricing/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["ChargeBasedPricingRulesets"],
    }),
    copyChargeBasedPricingToContract: builder.mutation<
      IItemResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `charge-based-pricing/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ChargeBasedPricingRulesets", "ContractFeeSchedules"],
    }),
    getChargeBasedPricingPropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `charge-based-pricing/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `chargeBasedPricing:${id}` },
      ],
    }),
    applyChargeBasedPricingPropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `charge-based-pricing/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "ChargeBasedPricingRulesets",
        { type: "DefaultRulePropagation", id: `chargeBasedPricing:${id}` },
      ],
    }),
  }),
});

export const {
  useGetAllChargeBasedPricingQuery,
  useGetChargeBasedPricingByIdQuery,
  useCreateChargeBasedPricingMutation,
  useUpdateChargeBasedPricingMutation,
  useDeleteChargeBasedPricingMutation,
  useCloneChargeBasedPricingAsDefaultMutation,
  useCopyChargeBasedPricingToContractMutation,
  useGetChargeBasedPricingPropagationPreviewQuery,
  useLazyGetChargeBasedPricingPropagationPreviewQuery,
  useApplyChargeBasedPricingPropagationMutation,
} = chargeBasedPricingQueries;
